import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import PropTypes from "prop-types";
import ReCAPTCHA from "react-google-recaptcha";
import { RecaptchaConfig } from "../../helpers/reviewHelper";
import { submitReview } from "../../api/reviewApi";
import RatingSummary from "./ratingSummary";
import RatingField from "./ratingField";
import ReviewList from "./reviewList";
import { getProductReviews } from "../../store/reviews/actions";

export default function Review({
    productId,
    productName,
    productImageUrl,
    district,
    schoolId,
    siteUserId,
    reviewTypes,
    product,
    manufacturerName,
    isTypical,
    openReviewBox = false,
    hideReviewList = false,
    typicalLayoutCartId,
}) {
    const dispatch = useDispatch();

    const [filter, setFilter] = useState({
        start: 0,
        length: 4,
        sort: 0,
        productId,
        isTypical: isTypical,
        typicalLayoutCartId: typicalLayoutCartId
    });
    const { reviewsList } = useSelector((state) => state.reviews);

    const [reviewerType, setReviewerType] = useState(
        reviewTypes.length > 0 ? reviewTypes[0].id : 0
    );

    const [ratings, setRatings] = useState({
        overall: 0,
        durability: 0,
        comfort: 0,
        functionality: 0,
        value: 0,
    });
    const [title, setTitle] = useState("");
    const [description, setDescription] = useState("");
    const [recaptchaToken, setRecaptchaToken] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [message, setMessage] = useState(null);
    const [showReviewBox, setShowReviewBox] = useState(openReviewBox);

    const [total, setTotal] = useState(parseInt(product?.reviewCount));
    const [loadingMore, setLoadingMore] = useState(false);


    const loadMoreReviews = () => {
        setLoadingMore(true);
        const newStart = filter.start + filter.length;
        const newFilter = { ...filter, start: newStart };

        dispatch(getProductReviews(newFilter)).then(() => {
            setFilter(newFilter);
            setLoadingMore(false);
        });
    };

    useEffect(() => {
        setTotal(parseInt(product?.reviewCount) || 0);
    }, [product]);

    useEffect(() => {
        const initialFilter = { ...filter, start: 0 };
        setFilter(initialFilter);
        dispatch(getProductReviews(initialFilter));
    }, [dispatch, productId]);

    const handleRatingChange = (name, value) => {
        setRatings((prev) => ({ ...prev, [name]: value }));
    };

    const submit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        setMessage(null);

        const reviewObj = {
            SiteUserId: siteUserId,
            ProductId: productId,
            DistrictId: parseInt(district?.id),
            SchoolId: schoolId,
            OverallRating: ratings.overall,
            DurabilityRating: ratings.durability,
            ComfortRating: ratings.comfort,
            FunctionalityRating: ratings.functionality,
            ValueRating: ratings.value,
            Title: title,
            Description: description,
            IsSchoolVerified: 0,
            IsApproved: 0,
            RecaptchaToken: recaptchaToken,
            ReviewerType: reviewerType || 1,
            IsTypical: isTypical ?? false,
            TypicalLayoutCartId: typicalLayoutCartId ?? 0,
        };

        try {
            const res = await submitReview(reviewObj);
            if (res?.data?.success) {
                setMessage("Submitted! Thanks (Pending moderation)");
                setShowReviewBox(false);
                setRatings({
                    overall: 0,
                    durability: 0,
                    comfort: 0,
                    functionality: 0,
                    value: 0,
                });
                setTitle("");
                setDescription("");
                setRecaptchaToken(null);
                dispatch(getProductReviews(filter));
            } else {
                setRecaptchaToken(null);
                setMessage(res?.data?.message);
            }
        } catch (err) {
            setMessage("Error: " + err.message);
        } finally {
            setSubmitting(false);
        }
    };

    const toggleReviewBox = () => setShowReviewBox((prev) => !prev);

    return (
        <div className="review-colleapse">
            {district?.isReviewVisible && (
                <RatingSummary
                    ratingData={{
                        averageRating: product?.averageRating || 0,
                        reviewCount: product?.reviewCount || 0,
                        starPercentages:
                            product?.starPercentages || {},
                    }}
                />

            )}
            <h5>Review this product</h5>
            <div className="product-detail-list">
                <p>Share your thoughts with other schools</p>
            </div>
            {message && <div style={{ marginTop: 12, fontWeight: "bold", color: "green" }}>{message}</div>}
            <button type="button" className="prod-detail-cta" onClick={toggleReviewBox}>
                Write a Product review
            </button>

            {showReviewBox && (
                <form onSubmit={submit} className="product-review-form">
                    {/* Product Info */}
                    {productImageUrl && (
                        <div className="product-user-info">
                            <div className="product-img">
                                <img
                                    src={productImageUrl}
                                    alt={productName}
                                />
                            </div>
                            <div>
                                <h5 className="summary-est-subhead">{productName}</h5>
                                <div className="summary-est-desc">
                                    {district?.name} | {schoolId} | {new Date().toLocaleDateString()}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Reviewer Type */}
                    <h5>Reviewer Type</h5>
                    <div className="reviewer-type-list">
                        {reviewTypes.map((rt) => (
                            <label htmlFor={rt.id} key={rt.id}>
                                <input
                                    type="radio"
                                    name="revtype"
                                    value={rt.id}
                                    id={rt.id}
                                    checked={reviewerType === rt.id}
                                    onChange={() => setReviewerType(rt.id)}
                                />
                                {rt.label || rt.type}
                            </label>
                        ))}
                    </div>

                    {/* Ratings */}
                    <div className="cart-side-notes" style={{ paddingBottom: 0 }}>
                        {Object.keys(ratings).map((field) => (
                            <RatingField
                                key={field}
                                label={field.charAt(0).toUpperCase() + field.slice(1)}
                                name={field}
                                value={ratings[field]}
                                onChange={handleRatingChange}
                            />
                        ))}
                    </div>

                    {/* Review Inputs */}
                    <div className="cart-side-notes">
                        <label className="cart-side-label">Write a Review</label>
                        <div className="cart-side-input">
                            <textarea
                                rows={2}
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                            />
                        </div>
                        <label className="cart-side-label">Title of Review (required)</label>
                        <div className="cart-side-input">
                            <input
                                type="text"
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                placeholder="What's most important to know?"
                                required
                            />
                        </div>
                    </div>

                    {/* reCAPTCHA */}
                    <div>
                        <ReCAPTCHA
                            sitekey={RecaptchaConfig.RECAPTCHA_SITE_KEY}
                            onChange={setRecaptchaToken}
                            onErrored={() =>
                                setMessage("reCAPTCHA failed to load. Please refresh the page.")
                            }
                            onExpired={() => setMessage("reCAPTCHA expired. Please verify again.")}
                        />
                    </div>

                    {/* Submit */}
                    <div className="product-power-wrap ">
                        <button
                            type="submit"
                            className="add-cart-cta"
                            disabled={submitting || !recaptchaToken}
                        >
                            {submitting ? "Submitting..." : "Submit Review"}
                        </button>
                    </div>
                </form>
            )}
            {!hideReviewList && (
                <ReviewList
                    reviews={reviewsList}
                    total={total}
                    isTypical={isTypical}
                    manufacturerName={manufacturerName}
                    loadMoreReviews={loadMoreReviews}
                    loadingMore={loadingMore}
                />
            )}
        </div>
    );
}

Review.propTypes = {
    productId: PropTypes.number.isRequired,
    productName: PropTypes.string.isRequired,
    productImageUrl: PropTypes.string,
    district: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        name: PropTypes.string,
        isReviewVisible: PropTypes.bool,
    }),
    schoolId: PropTypes.number,
    siteUserId: PropTypes.string,
    reviewTypes: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.number.isRequired,
            label: PropTypes.string,
            type: PropTypes.string,
        })
    ),
    product: PropTypes.shape({
        averageRating: PropTypes.number,
        reviewCount: PropTypes.number,
        starPercentages: PropTypes.object,
        reviews: PropTypes.array,
    }),
    manufacturerName: PropTypes.string,
    isTypical: PropTypes.bool,
    openReviewBox: PropTypes.bool,
    hideReviewList: PropTypes.bool,
    typicalLayoutCartId: PropTypes.number,
};

Review.defaultProps = {
    productImageUrl: "",
    district: { id: 0, name: "", isReviewVisible: true },
    schoolId: 0,
    siteUserId: "",
    reviewTypes: [],
    product: { reviews: [] },
    manufacturerName: "",
    isTypical: false,
    openReviewBox: false,
    hideReviewList: false,
    typicalLayoutCartId: 0
};
